import type { StoryObj } from '@storybook/html'
import type { CobaltButton as CobaltButtonProps } from './cobalt-button'
import { ICONS } from '@pollux-docaposte/icon-library'

const iconsAvailable = Object.keys(ICONS)

export default {
  title: 'Components/Action/cobalt-button',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which appear inside the button. It is **required**.'
    },
    type: {
      control: 'select',
      options: ['button', 'reset', 'submit'],
      description: "A string indicating the behavior of the button. It relies on `HTMLButtonElement['type']`",
      table: {
        defaultValue: { summary: 'button' }
      }
    },
    variant: {
      control: 'select',
      options: ['primary', 'secondary', 'tertiary'],
      description: 'A string indicating the design variation of the button based on the level of importance.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'primary' }
      }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the button. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    size: {
      control: 'select',
      options: ['small', 'medium', 'large'],
      description: 'A string indicating the size variation of the button.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'medium' }
      }
    },
    iconName: {
      type: { name: 'string' },
      options: iconsAvailable,
      control: { type: 'select' },
      description:
        'Accept all icon names from `@pollux-docaposte/icon-library`. Check icons available in the control column.\nThe icon render only if `iconName` and `iconPosition` are defined.',
      table: {
        type: { summary: `${JSON.stringify(iconsAvailable).substring(0, 100)}...` }
      }
    },
    iconPosition: {
      control: 'radio',
      options: ['left', 'right'],
      description: 'It allows to render the chosen icon on the left or on the right.\nThe icon render only if `iconName` and `iconPosition` are defined.',
      table: {
        type: { summary: 'string' }
      }
    },
    cobaltButtonClick: {
      description: 'Custom event emitted when button is clicked',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltButtonClick={handleClick}\nVue:@cobaltButtonClick="handleClick"\nAngular:(cobaltButtonClick)="handleClick()"\nVanillaJs:.addEventListener("cobaltButtonClick", (event) => {//handle click});'
        }
      },

      action: 'cobaltButtonClick'
    }
  }
}

const Template = (args: CobaltButtonProps) => {
  return `<cobalt-button type="${args.type}" label="${args.label}" variant="${args.variant}" size="${args.size}" disabled="${args.disabled}" icon-name="${args.iconName}" icon-position="${args.iconPosition}"></cobalt-button>`
}

export const Default: StoryObj = Template.bind({})

Default.args = {
  label: 'Button',
  type: 'button',
  variant: 'primary',
  size: 'medium',
  disabled: false,
  iconName: undefined,
  iconPosition: undefined
}

const TemplateClick = (args: CobaltButtonProps) => {
  return `
<script>
  document.querySelector('#action-button').addEventListener('cobaltButtonClick', (e) => {
    alert('Custom click event fired!', e)
  })
</script>
<cobalt-button id="action-button" label="${args.label}"></cobalt-button>`
}

export const OnClickEvent: StoryObj = TemplateClick.bind({})

OnClickEvent.args = {
  label: 'Button'
}
